import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { N300, P100, Y200 } from '@atlaskit/theme/colors';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import {
  DynamicResultTable,
  EntitlementIcon,
  SandboxLozenge,
  renderCustomPromotionDetails,
  renderPromotionValue,
  useFeatureToggle,
  withApollo,
} from '@css-xp/base';
import { GetOrderPreviewDto, SaleTransitionDetails } from '@atlassian/cssxp-ccp-types';
import { TransitionTime } from '@atlassian/cssxp-typescommon';
import { SelectedPromotion } from '@css-xp/base/forms';
import { formatToUSD } from '../utils/currency-display';
import {
  BillingFrequency,
  EntitlementDetails,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { Estimate } from './price-difference/price-difference';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { getProductName } from '@css-xp/base/entitlement-utils';
import { ENABLE_GLP_BILL_ESTIMATION, ENABLE_MANAGE_SUBSCRIPTION_BULK_EDIT } from '@atlassian/cssxp-utils-generic';
import { getBillPeriod } from '../../../utils/get-bill-period';
import { getAppliedPromotions, getDiscountAmount } from '../manage-entitlement-helper';
import { FormState } from '../manage-entitlement-v2';
import './product-family-table-v2.scss';
import { getOrderPreviewMessage } from './review-change-helper';

interface LinkedDestinationEntitlementWithPromotions extends LinkedDestinationEntitlement {
  appliedPromotions?: SelectedPromotion[];
  offeringLabel?: string;
  skipTrial?: boolean;
  pricingPlanLabel?: string;
  units?: string;
  orderTransitionTime?: TransitionTime;
  saleTransitionDetails?: SaleTransitionDetails;
  skipTrialEligible?: boolean;
  hasOptedForUpgradeTrial?: boolean;
}

export interface OrderPreviewData {
  response?: GetOrderPreviewDto;
  loading: boolean;
}

export interface ProductFamilyTableProps {
  entitlement: EntitlementDetails;
  linkedEntitlements?: LinkedDestinationEntitlement[];
  estimatedBill: Estimate;
  formState: FormState;
  isBillingCycleChanged?: boolean;
  isMonthlySelected?: boolean;
  orderPreviewData?: OrderPreviewData;
  isLoading?: boolean;
  showBillEstimateWarning?: boolean;
}

const emptyStateOrderProp = {
  header: '',
  description: 'No other subscriptions',
  imageUrl: '',
  testId: 'no-results-subscriptions-apps',
};

export const ProductFamilyTableV2 = ({
  entitlement,
  linkedEntitlements = [],
  estimatedBill,
  formState,
  isBillingCycleChanged,
  isMonthlySelected,
  orderPreviewData,
  isLoading = false,
  showBillEstimateWarning = false,
}: ProductFamilyTableProps) => {
  const [isEnabled] = useFeatureToggle();
  const isStatuspageProduct = isStatuspage(entitlement?.productId);
  // flag to display glp values instead of bill estimate values
  const isGLPEstimationEnabled =
    isEnabled(ENABLE_GLP_BILL_ESTIMATION) &&
    isEnabled(ENABLE_MANAGE_SUBSCRIPTION_BULK_EDIT) &&
    isBillingCycleChanged &&
    !isStatuspageProduct;

  const isEstimateLoading = !!estimatedBill?.loading || !!orderPreviewData?.loading || isLoading;
  const currentEntitlementEstimate = estimatedBill?.response?.estimate;
  const linkedEntitlementsBillEstimate = estimatedBill?.response?.linkedEstimates;

  const finalLinkedEntitlements: Array<LinkedDestinationEntitlementWithPromotions> = useMemo(() => {
    let finalLinkedEntitlementsWithEstimates: LinkedDestinationEntitlementWithPromotions[] = [];
    if (
      (isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL) &&
      linkedEntitlements?.length > 0
    ) {
      finalLinkedEntitlementsWithEstimates = linkedEntitlements.map(
        (linkedEnt: LinkedDestinationEntitlementWithPromotions) => {
          const billEstimate = linkedEntitlementsBillEstimate?.find(
            (item) => item?.entitlementId === linkedEnt?.entitlementId
          );
          const orderPreviewDetail = orderPreviewData?.response?.items?.find(
            (item) => item.processingInfo?.entitlement?.id === linkedEnt?.entitlementId
          );

          if (billEstimate) {
            linkedEnt.grandTotal = billEstimate.grandTotal;
            linkedEnt.discount = getDiscountAmount(billEstimate.items);
            linkedEnt.billPeriod = getBillPeriod(billEstimate.items);
            linkedEnt.saleTransitionDetails = billEstimate.saleTransitionDetails;
          }

          linkedEnt.orderTransitionTime = orderPreviewDetail?.processingInfo?.transitionTime;
          linkedEnt.appliedPromotions = getAppliedPromotions(
            formState[linkedEnt?.entitlementId],
            isBillingCycleChanged,
            linkedEnt
          );
          linkedEnt.offeringLabel = formState[linkedEnt?.entitlementId]?.offering?.label;
          linkedEnt.skipTrial = formState[linkedEnt?.entitlementId]?.skipTrial;
          linkedEnt.pricingPlanLabel = formState[linkedEnt?.entitlementId]?.pricingPlan?.label;
          linkedEnt.userTier = formState[linkedEnt?.entitlementId]?.userTier?.value;
          linkedEnt.skipTrialEligible = formState[linkedEnt?.entitlementId]?.skipTrialEligible;
          linkedEnt.hasOptedForUpgradeTrial = formState[linkedEnt?.entitlementId]?.hasOptedForUpgradeTrial;

          return linkedEnt;
        }
      );
    }

    const orderPreviewDetail = orderPreviewData?.response?.items?.find(
      (item) => item.processingInfo?.entitlement?.id === entitlement?.id
    );
    const currentEntitlementDetails: LinkedDestinationEntitlementWithPromotions = {
      edition: formState?.offering?.label,
      entitlementId: entitlement?.id,
      entitlementNumber: entitlement?.entitlementNumber,
      isSandbox: entitlement?.isSandbox,
      logoId: entitlement?.logoId,
      productId: entitlement?.productId,
      parentSen: entitlement?.parentSen,
      productName: entitlement?.productName,
      grandTotal: currentEntitlementEstimate?.grandTotal,
      saleTransitionDetails: currentEntitlementEstimate?.saleTransitionDetails,
      discount: getDiscountAmount(currentEntitlementEstimate?.items),
      billPeriod: getBillPeriod(currentEntitlementEstimate?.items),
      userTier: formState?.userTier?.value,
      units: formState?.units,
      appliedPromotions: getAppliedPromotions(formState, isBillingCycleChanged, entitlement),
      offeringLabel: formState?.offering?.label,
      skipTrial: formState?.skipTrial,
      pricingPlanLabel: formState?.pricingPlan?.label,
      orderTransitionTime: orderPreviewDetail?.processingInfo?.transitionTime,
      skipTrialEligible: formState?.skipTrialEligible,
      hasOptedForUpgradeTrial: formState?.hasOptedForUpgradeTrial,
    };

    finalLinkedEntitlementsWithEstimates.splice(0, 0, currentEntitlementDetails);
    return finalLinkedEntitlementsWithEstimates;
  }, [
    linkedEntitlements,
    entitlement,
    currentEntitlementEstimate,
    linkedEntitlementsBillEstimate,
    formState,
    isBillingCycleChanged,
    orderPreviewData,
  ]);

  const isAnyEntitlementOnTrial = useMemo(() => {
    return finalLinkedEntitlements.some((entitlement) => entitlement.skipTrialEligible && !entitlement.skipTrial);
  }, [finalLinkedEntitlements]);

  const appliedDiscount = (promotion: SelectedPromotion) => {
    return (
      <span>
        {promotion?.customFormField
          ? renderCustomPromotionDetails(promotion)
          : renderPromotionValue(promotion, {
              showDuration: true,
              showLabelsInUpperCase: true,
              showPercentageLabel: true,
              showBenefitType: false,
              showReason: !!promotion.isPromotionCustomisable,
              showReasonNextLine: false,
            })}
      </span>
    );
  };

  const discountDetails = (appliedPromotions: SelectedPromotion[]) => {
    return (
      <>
        {isGLPEstimationEnabled && <div>Discounts aren’t included in the price estimate at this time.</div>}
        <div>Discounts applied:</div>
        {appliedPromotions?.map((promotion: SelectedPromotion, index: number) => {
          return (
            <div className="applied-discounts" key={index}>
              Discount #{index + 1}: {appliedDiscount(promotion)}
            </div>
          );
        })}
      </>
    );
  };

  const renderPrimitiveTooltip = () => {
    return styled(TooltipPrimitive)`
      background: #172b4d;
      border-radius: 3px;
      color: #ffffff;
      padding: 10px;
      font-weight: 500;
      font-size: 12px;
      line-height: 14px;
    `;
  };

  const nextPlanDetails = (item: LinkedDestinationEntitlementWithPromotions) => {
    return <div>{`Next Price Estimate: ${formatToUSD((item?.saleTransitionDetails?.glpForNextPlan || 0) / 100)}`}</div>;
  };

  const getRows = () => {
    return finalLinkedEntitlements.map((item: LinkedDestinationEntitlementWithPromotions, index) => {
      const shouldShowDiscounts = isGLPEstimationEnabled ? !!item?.appliedPromotions?.length : true;
      const cellData = [
        {
          content: (
            <div className="subscription-details">
              <EntitlementIcon
                logoId={item?.logoId as string}
                productId={item?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <span className="product-name">{getProductName(item)}</span>
              {item?.isSandbox && <SandboxLozenge />}
              <div className="entitlement-details">
                <div>{item?.offeringLabel}</div>
                {!isGLPEstimationEnabled && <div>{item?.billPeriod}</div>}
                <div>{item?.entitlementNumber}</div>
                {item?.skipTrialEligible && <div>Skip trial: {item?.skipTrial ? 'Yes' : 'No'}</div>}
                <div>Price plan: {item?.pricingPlanLabel ?? 'Not available'}</div>
              </div>
              {item.orderTransitionTime && (
                <div className="order-preview-detail" data-testid="order-preview-detail">
                  <InfoIcon size="small" testId="info-icon" label="" primaryColor={N300} />
                  <p>{getOrderPreviewMessage(item.orderTransitionTime)}</p>
                </div>
              )}
              {item.hasOptedForUpgradeTrial && (
                <div className="upgrade-trial-message">
                  <WarningIcon label="" primaryColor={Y200} />
                  <p>Current plan will be charged during the no-cost trial period of Premium plan</p>
                </div>
              )}
            </div>
          ),
        },
        {
          content: shouldShowDiscounts && (
            <>
              {isGLPEstimationEnabled ? 'unavailable' : formatToUSD(item?.discount ?? 0)}
              {!!item?.appliedPromotions?.length && (
                <span data-testid="discount-tooltip" className="discount-tooltip">
                  <Tooltip
                    content={discountDetails(item?.appliedPromotions as SelectedPromotion[])}
                    position="bottom-end"
                    tag="span"
                    component={renderPrimitiveTooltip()}
                  >
                    <EditorPanelIcon label="tax-tooltip-info" />
                  </Tooltip>
                </span>
              )}
            </>
          ),
        },
        {
          content: <>{isStatuspageProduct ? item?.units : item?.userTier}</>,
        },
        {
          content: isGLPEstimationEnabled ? (
            formatToUSD((item?.saleTransitionDetails?.glpForNextPlan ?? 0) / 100)
          ) : (
            <>
              {formatToUSD(item?.grandTotal ?? 0)}
              {item?.saleTransitionDetails?.glpForCurrentPlan !== item?.saleTransitionDetails?.glpForNextPlan && (
                <span data-testid="price-tooltip" className="price-tooltip">
                  <Tooltip
                    content={nextPlanDetails(item)}
                    position="bottom-end"
                    tag="span"
                    component={renderPrimitiveTooltip()}
                  >
                    <EditorPanelIcon label="price-tooltip-info" />
                  </Tooltip>
                </span>
              )}
            </>
          ),
        },
      ];

      if (isMonthlySelected && !isStatuspageProduct) {
        cellData.splice(2, 1);
      }

      return {
        key: item.entitlementId ? item.entitlementId : index.toString(),
        cells: cellData,
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Subscription',
        shouldTruncate: true,
        width: 35,
      },
      {
        content: 'Discount applied',
        width: 15,
      },
      {
        content: isStatuspageProduct ? 'Units' : 'User tier/agents',
        width: 10,
      },
      {
        content: 'Price',
        width: 15,
      },
    ];

    if (isMonthlySelected && !isStatuspageProduct) {
      cells.splice(2, 1);
    }

    return {
      cells,
    };
  };

  return (
    <div className="product-family-table-v2-container" data-testid="product-family-table-container">
      {showBillEstimateWarning &&
        isAnyEntitlementOnTrial &&
        (isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL) && (
          <div className="warning-message">
            <InfoIcon size="small" testId="info-icon" label="Info" primaryColor={P100} />
            <b>Keep in mind -</b>
            <span>
              prices are based on the full cycle estimate, and don’t include trial periods or pro-rated trial period
              amounts.
            </span>
          </div>
        )}
      <DynamicResultTable
        testId="product-family-table"
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={isEstimateLoading}
        boxShadow={false}
        alternateRowColor={false}
      />
    </div>
  );
};

export default withApollo(ProductFamilyTableV2);
